import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Wrench, ArrowRight, Phone, Mail, MapPin, Clock, ShieldCheck, Cpu,
  Gauge, Package, Snowflake, Settings, CheckCircle2, MessageSquare,
  ClipboardCheck, ThumbsUp, ExternalLink, Home as HomeIcon, Store, Factory, Info, TrendingDown, Target, Timer
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { SEO } from '../components/SEO';
import { QuickLeadForm } from '../components/QuickLeadForm';
import { InteractiveDiagnostic } from '../components/InteractiveDiagnostic';
import { HeartbeatDivider } from '../components/HeartbeatDivider';
import { DowntimeCalculator } from '../components/DowntimeCalculator';
import { ROICalculator } from '../components/ROICalculator';
import { CaseStudies } from '../components/CaseStudies';
import { Testimonials } from '../components/Testimonials';
import { MachineSelectorQuiz } from '../components/MachineSelectorQuiz';
import { FeaturedDownloads } from '../components/FeaturedDownloads';
import { TrustBadges } from '../components/TrustBadges';
import { WorkshopGallery, IllustratedCapabilities } from '../components/WorkshopGallery';
import { PHOTO } from '../workshopPhotos';
import { CONTACT, PARTNERS, REBUILD_NOTICE_TITLE, REBUILD_NOTICE_BODY } from '../lyphline';

const HERO_IMG = PHOTO.brazing;
const REPAIRS_IMG = PHOTO.rewiring;
const RETROFITS_IMG = PHOTO.nitrogen;

/* ---------------- Hero (with inline quick-quote form) ---------------- */
const Hero = () => (
  <section className="relative overflow-hidden bg-[#003456]" data-testid="hero-section">
    <img src={HERO_IMG} alt="A LyphLine technician brazing refrigeration lines during a freeze dryer rebuild" className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#003456] via-[#003456]/90 to-[#003456]/50" />
    <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-16 grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <span className="inline-flex items-center gap-2 bg-[#CE8946]/15 text-[#CE8946] px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.2em] border border-[#CE8946]/30">
          <Snowflake className="h-4 w-4" /> Freeze Dryer Rebuild Specialists
        </span>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mt-6 leading-[1.08]">
          Freeze dryer quit on you?
          <span className="block text-[#CE8946] mt-2">We'll get it running again.</span>
        </h1>
        <p className="text-lg text-white/75 mt-6 max-w-xl leading-relaxed">
          We diagnose, rebuild and upgrade home, business and commercial freeze dryers of any make.
          No dealer runaround, just a straight answer and a firm quote.
        </p>
        <div className="flex flex-wrap gap-4 mt-8">
          <a href="#diagnose" className="inline-flex items-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold px-7 py-4 rounded-md transition-colors shadow-xl shadow-[#CE8946]/20" data-testid="hero-diagnose-btn">
            <Wrench className="h-5 w-5" /> Diagnose My Machine
          </a>
          <a href={CONTACT.phoneHref} className="inline-flex items-center gap-2 border border-white/25 text-white font-semibold px-7 py-4 rounded-md hover:bg-white/10 transition-colors" data-testid="hero-call-btn">
            <Phone className="h-5 w-5 text-[#CE8946]" /> {CONTACT.phone}
          </a>
        </div>
        <div className="flex flex-wrap gap-6 mt-8 text-sm text-white/60">
          <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#CE8946]" /> 90 day rebuild warranty</span>
          <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#CE8946]" /> Select makes &amp; models</span>
          <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-[#CE8946]" /> Fast turnaround</span>
        </div>
      </div>
      {/* Inline quick-quote form */}
      <div className="bg-white border border-slate-200 rounded-2xl p-7 shadow-2xl w-full max-w-md lg:ml-auto" data-testid="hero-quote-card">
        <h3 className="text-xl font-bold text-[#003456]">Get a free diagnostic quote</h3>
        <p className="text-slate-500 text-sm mt-1 mb-5">Tell us what's happening, we'll reply fast.</p>
        <QuickLeadForm mode="quote" source="hero-quick-quote" testid="hero-lead-form" />
        <TrustBadges variant="light" className="mt-5 pt-5 border-t border-slate-100" />
      </div>
    </div>
  </section>
);

/* ---------------- Segment selector (Home / Small Business / Commercial) ---------------- */
const SEGMENTS = {
  home: {
    label: 'Home', icon: HomeIcon,
    headline: 'Home freeze dryers, back in action',
    copy: 'Small and medium home units, home builds and popular consumer brands. Ship it in and we\'ll handle the rest.',
    sizes: 'Small & Medium units', examples: 'Home scale & residential machines', turnaround: '~5 to 10 business day turnaround',
  },
  'small-business': {
    label: 'Small & Medium Business', icon: Store,
    headline: 'Keep your small & mid size production running',
    copy: 'Large and extra large units for cafés, farms and growing food producers. Downtime costs you sales, we prioritise your rebuild.',
    sizes: 'Large & Extra Large units', examples: 'Cafés, farms, small & medium food producers', turnaround: 'Priority turnaround · emergency shipping available',
  },
  commercial: {
    label: 'Commercial', icon: Factory,
    headline: 'Production floor uptime, guaranteed',
    copy: 'Semi commercial and production equipment for manufacturers and labs. Production scheduling and rush options available.',
    sizes: 'Semi commercial & production', examples: 'Food manufacturers, nutraceutical & research labs', turnaround: 'Production scheduling available · rush options',
  },
};
const SegmentSelector = ({ segment, setSegment }) => {
  const s = SEGMENTS[segment];
  return (
    <section className="py-16 section-padding bg-white" data-testid="segment-selector">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-8">
          <span className="text-[#B87333] font-semibold text-xs uppercase tracking-[0.2em]">Who are we helping?</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#003456] mt-3">Service for every scale</h2>
        </div>
        <div className="flex justify-center gap-2 sm:gap-3 mb-8">
          {Object.entries(SEGMENTS).map(([key, v]) => (
            <button key={key} onClick={() => setSegment(key)}
              className={`inline-flex items-center gap-2 px-4 sm:px-6 py-3 rounded-full text-sm font-semibold transition-colors ${segment === key ? 'bg-[#003456] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid={`segment-${key}`}>
              <v.icon className="h-4 w-4" /> {v.label}
            </button>
          ))}
        </div>
        <div className="grid lg:grid-cols-3 gap-6 items-stretch">
          <div className="lg:col-span-1 bg-[#003456] rounded-2xl p-8 text-white">
            <s.icon className="h-9 w-9 text-[#CE8946]" />
            <h3 className="text-2xl font-bold mt-4">{s.headline}</h3>
            <p className="text-white/70 mt-3 text-sm leading-relaxed">{s.copy}</p>
            <a href="#diagnose" className="inline-flex items-center gap-2 mt-6 text-[#CE8946] font-bold" data-testid="segment-diagnose-link">Diagnose my machine <ArrowRight className="h-4 w-4" /></a>
          </div>
          <div className="lg:col-span-2 grid sm:grid-cols-3 gap-6">
            {[
              { label: 'Machine sizes', value: s.sizes, icon: Gauge },
              { label: 'Typical customers', value: s.examples, icon: Store },
              { label: 'Turnaround', value: s.turnaround, icon: Clock },
            ].map((c) => (
              <div key={c.label} className="bg-[#F1F5F9] border border-slate-200 rounded-2xl p-6" data-testid={`segment-card-${c.label.split(' ')[0].toLowerCase()}`}>
                <c.icon className="h-6 w-6 text-[#B87333]" />
                <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold mt-4">{c.label}</p>
                <p className="text-[#003456] font-semibold mt-1 leading-snug">{c.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- Machine tiers ---------------- */
const TIERS = [
  { n: '01', icon: HomeIcon, title: 'Residential Machines', copy: 'Home scale units pushed hard through harvest season, back to dependable everyday performance.', to: '/machines/residential' },
  { n: '02', icon: Store, title: 'Small Business Machines', copy: 'Large and extra large units earning their keep for cafés, farms and growing food producers.', to: '/machines/small-business' },
  { n: '03', icon: Factory, title: 'Semi Commercial Machines', copy: 'Production floor equipment where uptime is everything, rebuilt to last.', to: '/machines/semi-commercial' },
];
const MachineTiers = () => (
  <section className="py-20 section-padding bg-[#002B47]" data-testid="machine-tiers">
    <div className="max-w-7xl mx-auto">
      <div className="max-w-2xl mb-12">
        <span className="text-[#CE8946] font-semibold text-xs uppercase tracking-[0.2em]">What we service</span>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mt-3">We rebuild freeze dryers of varying sizes</h2>
        <p className="text-white/60 mt-3">From well used home units to production floor workhorses, our technicians know these machines inside and out.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {TIERS.map((t) => (
          <Link key={t.n} to={t.to} className="group bg-[#023F63] border border-white/10 rounded-2xl p-8 hover:border-[#CE8946]/50 transition-colors flex flex-col" data-testid={`tier-${t.n}`}>
            <div className="flex items-center justify-between mb-6">
              <span className="w-12 h-12 rounded-xl bg-[#CE8946]/15 flex items-center justify-center"><t.icon className="h-6 w-6 text-[#CE8946]" /></span>
              <span className="text-5xl font-extrabold text-white/5 group-hover:text-[#CE8946]/20 transition-colors">{t.n}</span>
            </div>
            <h3 className="text-xl font-bold text-white">{t.title}</h3>
            <p className="text-white/60 text-sm mt-2 leading-relaxed flex-1">{t.copy}</p>
            <span className="inline-flex items-center gap-2 mt-5 text-[#CE8946] font-bold text-sm">Explore rebuilds <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></span>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Rebuilds vs Retrofits tabs ---------------- */
const RepairsRetrofits = () => {
  const [tab, setTab] = useState('rebuilds');
  const data = {
    rebuilds: {
      img: REPAIRS_IMG, title: 'Rebuilds & Maintenance',
      copy: 'In most cases we restore your machine as a full or partial rebuild using quality aftermarket (non OEM) parts, vacuum pumps, refrigeration, control boards, seals and sensors, to get you back online fast.',
      points: ['Vacuum & refrigeration service', 'Control board diagnostics', 'Seal, gasket & sensor replacement', 'Full preventive tune ups'],
    },
    retrofits: {
      img: RETROFITS_IMG, title: 'Modifications',
      copy: 'Personal modifications tailor your machine to how you work, with upgraded durability, modern controls and custom changes for more reliability and longer life.',
      points: ['Upgraded components & controls', 'Performance enhancements', 'Durability improvements', 'Correcting engineering weak points'],
    },
  };
  const active = data[tab];
  return (
    <section id="rebuilds" className="py-20 section-padding bg-white" data-testid="repairs-retrofits">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
          <div className="max-w-xl">
            <span className="text-[#B87333] font-semibold text-xs uppercase tracking-[0.2em]">Two ways we help</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#003456] mt-3">Rebuilds &amp; Modifications</h2>
          </div>
          <div className="inline-flex bg-slate-100 rounded-lg p-1" id="retrofits">
            {['rebuilds', 'retrofits'].map((k) => (
              <button key={k} onClick={() => setTab(k)}
                className={`px-6 py-2.5 rounded-md text-sm font-semibold transition-colors ${tab === k ? 'bg-[#003456] text-white' : 'text-slate-600 hover:text-[#003456]'}`}
                data-testid={`tab-${k}`}>
                {k === 'rebuilds' ? 'Rebuilds' : 'Modifications'}
              </button>
            ))}
          </div>
        </div>
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div className="rounded-2xl overflow-hidden aspect-[4/3] bg-slate-100">
            <img src={active.img} alt={active.title} className="w-full h-full object-cover" />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-[#003456]">{active.title}</h3>
            <p className="text-slate-600 mt-4 leading-relaxed">{active.copy}</p>
            <ul className="grid sm:grid-cols-2 gap-3 mt-6">
              {active.points.map((p) => (
                <li key={p} className="flex items-start gap-2 text-slate-700 text-sm">
                  <CheckCircle2 className="h-5 w-5 text-[#B87333] flex-shrink-0 mt-0.5" /> {p}
                </li>
              ))}
            </ul>
            <a href="#diagnose" className="inline-flex items-center gap-2 mt-8 text-[#B87333] font-bold hover:gap-3 transition-all" data-testid="rr-diagnose-link">
              Diagnose my machine <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- Services grid ---------------- */
const SERVICES = [
  { icon: ClipboardCheck, title: 'Diagnostics & Assessment', copy: 'A thorough inspection to pinpoint exactly what your machine needs.' },
  { icon: Wrench, title: 'Rebuilds & Maintenance', copy: 'Full or partial rebuilds with quality aftermarket (non OEM) parts to restore reliable performance.' },
  { icon: Settings, title: 'Modifications', copy: 'Personal modifications and upgraded parts to tailor and future proof your unit.' },
  { icon: Package, title: 'Parts & Component Replacement', copy: 'Genuine and compatible components sourced and installed.' },
  { icon: ShieldCheck, title: 'Financing Support', copy: 'Flexible options so a rebuild never has to wait.' },
];
const ServicesGrid = () => (
  <section id="services" className="py-20 section-padding bg-[#F1F5F9]" data-testid="services-grid">
    <div className="max-w-7xl mx-auto">
      <div className="max-w-2xl mb-12">
        <span className="text-[#B87333] font-semibold text-xs uppercase tracking-[0.2em]">Full service rebuild shop</span>
        <h2 className="text-3xl sm:text-4xl font-bold text-[#003456] mt-3">Everything your freeze dryer needs</h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {SERVICES.map((s) => (
          <div key={s.title} className="group bg-white border border-slate-200 rounded-2xl p-8 hover:shadow-lg hover:border-[#CE8946]/50 transition-all" data-testid={`service-${s.title.split(' ')[0].toLowerCase()}`}>
            <span className="w-14 h-14 rounded-xl bg-[#003456] flex items-center justify-center mb-5 group-hover:bg-[#CE8946] transition-colors">
              <s.icon className="h-7 w-7 text-[#CE8946] group-hover:text-[#003456] transition-colors" />
            </span>
            <h3 className="text-lg font-bold text-[#003456]">{s.title}</h3>
            <p className="text-slate-600 text-sm mt-2 leading-relaxed">{s.copy}</p>
          </div>
        ))}
        <a href="#diagnose" className="flex flex-col justify-center bg-[#003456] rounded-2xl p-8 text-white hover:bg-[#023F63] transition-colors" data-testid="services-cta">
          <span className="text-lg font-bold">Not sure what's wrong?</span>
          <span className="text-white/60 text-sm mt-2">Use our interactive diagram, we'll figure it out.</span>
          <span className="inline-flex items-center gap-2 text-[#CE8946] font-bold mt-4">Get started <ArrowRight className="h-4 w-4" /></span>
        </a>
      </div>
    </div>
  </section>
);

/* ---------------- 4-step process ---------------- */
const STEPS = [
  { n: 1, icon: MessageSquare, title: 'Contact Us', copy: 'Tell us about your machine and the trouble you\'re seeing.' },
  { n: 2, icon: ClipboardCheck, title: 'Free Diagnostic Report', copy: 'We assess the unit and send a clear report and quote.' },
  { n: 3, icon: Wrench, title: 'Rebuild or Modification', copy: 'On your approval, our techs get to work restoring it.' },
  { n: 4, icon: ThumbsUp, title: 'Back to Business', copy: 'Your freeze dryer returns running like new.' },
];
const Process = () => (
  <section className="py-20 section-padding bg-[#003456]" data-testid="process-section">
    <div className="max-w-7xl mx-auto">
      <div className="text-center max-w-2xl mx-auto mb-14">
        <HeartbeatDivider className="mb-5" />
        <span className="text-[#CE8946] font-semibold text-xs uppercase tracking-[0.2em]">How it works</span>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mt-3">Back up and running in four steps</h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {STEPS.map((s, i) => (
          <div key={s.n} className="relative bg-[#023F63] border border-white/10 rounded-2xl p-7" data-testid={`step-${s.n}`}>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-11 h-11 rounded-full bg-[#CE8946] text-[#003456] font-extrabold flex items-center justify-center">{s.n}</span>
              <s.icon className="h-6 w-6 text-[#CE8946]" />
            </div>
            <h3 className="text-lg font-bold text-white">{s.title}</h3>
            <p className="text-white/60 text-sm mt-2 leading-relaxed">{s.copy}</p>
            {i < STEPS.length - 1 && <ArrowRight className="hidden lg:block absolute top-1/2 -right-4 h-6 w-6 text-[#CE8946]/40" />}
          </div>
        ))}
      </div>
      <div className="text-center mt-12">
        <Link to="/repair/quote" className="inline-flex items-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold px-8 py-4 rounded-md transition-colors" data-testid="process-cta">
          Start My Free Quote <ArrowRight className="h-5 w-5" />
        </Link>
      </div>
    </div>
  </section>
);

/* ---------------- Callback strip ---------------- */
const CallbackStrip = () => (
  <section className="py-16 section-padding bg-[#023F63]" data-testid="callback-strip">
    <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-center">
      <div>
        <span className="text-[#CE8946] font-semibold text-xs uppercase tracking-[0.2em]">Prefer to talk it through?</span>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mt-3">Request a callback</h2>
        <p className="text-white/60 mt-3 max-w-md">Leave your number and the best time to reach you, a real technician will call you back to talk through your machine.</p>
        <a href={CONTACT.phoneHref} className="inline-flex items-center gap-2 mt-6 text-white font-semibold hover:text-[#CE8946] transition-colors">
          <Phone className="h-5 w-5 text-[#CE8946]" /> Or call now: {CONTACT.phone}
        </a>
      </div>
      <div className="bg-white border border-slate-200 shadow-2xl rounded-2xl p-7">
        <QuickLeadForm mode="callback" source="callback-request" testid="callback-form" />
      </div>
    </div>
  </section>
);

/* ---------------- Contact cards ---------------- */
const ContactCards = () => {
  const cards = [
    { icon: Phone, label: 'Call the rebuild desk', value: CONTACT.phone, href: CONTACT.phoneHref, cta: 'Tap to call' },
    { icon: Mail, label: 'Email our technicians', value: CONTACT.email, href: `mailto:${CONTACT.email}`, cta: 'Send an email' },
    { icon: MapPin, label: 'Rebuild shop', value: CONTACT.address, href: null, cta: CONTACT.hours },
  ];
  return (
    <section className="py-20 section-padding bg-white" data-testid="contact-cards">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <HeartbeatDivider className="mb-5" />
          <span className="text-[#B87333] font-semibold text-xs uppercase tracking-[0.2em]">Talk to a real technician</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#003456] mt-3">Reach us your way</h2>
          <p className="text-slate-600 mt-3">{CONTACT.serviceArea}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {cards.map((c) => {
            const inner = (
              <>
                <span className="w-14 h-14 rounded-xl bg-[#CE8946]/15 flex items-center justify-center mb-5"><c.icon className="h-7 w-7 text-[#B87333]" /></span>
                <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">{c.label}</p>
                <p className="text-lg font-bold text-[#003456] mt-2 leading-snug">{c.value}</p>
                <span className="inline-flex items-center gap-1.5 text-[#B87333] font-semibold text-sm mt-4">{c.cta}</span>
              </>
            );
            return c.href ? (
              <a key={c.label} href={c.href} className="group bg-[#F1F5F9] border border-slate-200 rounded-2xl p-8 hover:border-[#CE8946] hover:shadow-lg transition-all" data-testid={`contact-card-${c.label.split(' ')[0].toLowerCase()}`}>{inner}</a>
            ) : (
              <div key={c.label} className="bg-[#F1F5F9] border border-slate-200 rounded-2xl p-8" data-testid="contact-card-ship">{inner}</div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Cross-links to sister brands ---------------- */
const SisterBrands = () => (
  <section className="py-16 section-padding bg-[#F1F5F9]" data-testid="sister-brands">
    <div className="max-w-5xl mx-auto text-center">
      <h2 className="text-2xl sm:text-3xl font-bold text-[#003456]">Looking to buy a new freeze dryer?</h2>
      <p className="text-slate-600 mt-3">We rebuild every brand, and we're proud partners of these product lines.</p>
      <div className="grid sm:grid-cols-2 gap-5 mt-8 text-left">
        {[PARTNERS.home, PARTNERS.commercial].map((p) => (
          <a key={p.name} href={p.url} target="_blank" rel="noopener noreferrer" className="group flex items-center justify-between gap-4 bg-white border border-slate-200 hover:border-[#CE8946] rounded-xl px-7 py-6 transition-colors shadow-sm" data-testid={`brand-link-${p.name.toLowerCase()}`}>
            <div>
              <div className="text-xl font-extrabold text-[#003456] group-hover:text-[#B87333] transition-colors">{p.name}</div>
              <div className="text-sm text-slate-500 mt-1">{p.blurb}</div>
            </div>
            <ExternalLink className="h-5 w-5 text-slate-400 group-hover:text-[#B87333] transition-colors flex-shrink-0" />
          </a>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Rebuild / aftermarket band ---------------- */
const RebuildBand = () => (
  <section className="bg-[#023F63] section-padding py-5 border-b border-white/10" data-testid="rebuild-band">
    <div className="max-w-7xl mx-auto flex items-start gap-3">
      <Info className="h-5 w-5 text-[#CE8946] flex-shrink-0 mt-0.5" />
      <p className="text-sm text-white/75"><strong className="text-white">{REBUILD_NOTICE_TITLE}</strong> {REBUILD_NOTICE_BODY}</p>
    </div>
  </section>
);

/* ---------------- Economics of downtime ---------------- */
const COSTS = [
  { icon: Timer, title: "Time you can't get back", copy: "Every day offline is a day of batches that never happen, and product you can't sell." },
  { icon: TrendingDown, title: 'More than the rebuild bill', copy: 'Idle equipment, spoiled runs and rush fees stack up fast against fixed overhead.' },
  { icon: Target, title: 'Opportunity that walks away', copy: 'Missed orders, empty shelves and customers who find another supplier, momentum is the hardest thing to rebuild.' },
];
const EconomicsSection = () => (
  <section className="py-20 section-padding bg-[#003456]" data-testid="economics-section">
    <div className="max-w-7xl mx-auto">
      <div className="max-w-3xl">
        <span className="text-[#CE8946] font-semibold text-xs uppercase tracking-[0.2em]">The real cost of downtime</span>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mt-3">Every hour it's down is money you're not making</h2>
        <p className="text-white/70 mt-4 leading-relaxed">
          Plenty of growing food businesses run on residential freeze dryers, they're affordable and surprisingly capable.
          But push a home grade machine through commercial duty cycles and, eventually, it stops. When it does, the loss is
          far bigger than a rebuild bill: it's the orders you can't fill and the growth you put on hold.
        </p>
      </div>
      <div className="grid md:grid-cols-3 gap-6 mt-12">
        {COSTS.map((c) => (
          <div key={c.title} className="bg-[#023F63] border border-white/10 rounded-2xl p-8" data-testid={`cost-${c.title.split(' ')[0].toLowerCase()}`}>
            <span className="w-14 h-14 rounded-xl bg-[#CE8946]/15 flex items-center justify-center mb-5"><c.icon className="h-7 w-7 text-[#CE8946]" /></span>
            <h3 className="text-lg font-bold text-white">{c.title}</h3>
            <p className="text-white/60 text-sm mt-2 leading-relaxed">{c.copy}</p>
          </div>
        ))}
      </div>
      <div className="mt-12 flex flex-col sm:flex-row sm:items-center gap-5 bg-[#023F63] border border-[#CE8946]/30 rounded-2xl p-6">
        <p className="text-white/85 text-lg flex-1">
          A fast, dependable <span className="text-[#CE8946] font-semibold">rebuild</span> protects far more than your machine, it protects the business you've built around it.
        </p>
        <a href="#diagnose" className="inline-flex items-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold px-7 py-4 rounded-md transition-colors whitespace-nowrap" data-testid="economics-cta">
          Get back online <ArrowRight className="h-5 w-5" />
        </a>
      </div>
    </div>
  </section>
);

const Home = () => {
  const [segment, setSegment] = useState('home');
  const [downtime, setDowntime] = useState(null);

  // Scroll to a hash target (e.g. /#downtime, /#services) once the page has rendered.
  useEffect(() => {
    const hash = window.location.hash?.replace('#', '');
    if (!hash) return;
    const t = setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 400);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-screen">
      <SEO
        title="Freeze Dryer Rebuilds, Modifications & Diagnostics"
        description="LyphLine Freeze Dryers rebuilds and modifies freeze dryers of select makes and varying sizes using quality aftermarket (non OEM) parts. Use our interactive diagnostic or get a free quote."
        keywords="freeze dryer rebuild, freeze dryer rebuild, freeze dryer modification, aftermarket freeze dryer parts, commercial freeze dryer rebuild, LyphLine"
      />
      <Navbar />
      <Hero />
      <RebuildBand />
      <SegmentSelector segment={segment} setSegment={setSegment} />
      <EconomicsSection />
      <DowntimeCalculator onEstimate={setDowntime} />
      <ROICalculator />
      <CaseStudies />
      <Testimonials />
      <MachineTiers />
      <MachineSelectorQuiz />
      <RepairsRetrofits />
      <IllustratedCapabilities />
      <InteractiveDiagnostic segment={segment} downtime={downtime} />
      <ServicesGrid />
      <WorkshopGallery />
      <FeaturedDownloads />
      <Process />
      <CallbackStrip />
      <ContactCards />
      <SisterBrands />
      <Footer />
    </div>
  );
};

export default Home;
